import WRAP from "WRAP-JS";
import { _orderBy, parseUTCDate } from "../../../utils/FPEUtils";
import LegendSst from "../legend/LegendSst";
import { WX_ID_SST } from "../../../store/WeatherContentsState";
import TimeController from "../helper/TimeController";
import { configSstDh, configSstLayer } from "./config/ConfigSst";
import MissingDataObserver from "../helper/MissingDataObserver";
import { COMMUNICATE_ERROR_MESSAGE_SST, COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER } from "../../../constants/ErrorMessage";

const MISSING_THRESHOLD = {
  [WX_ID_SST]: 187200
}

class Sst {
  static NAME = WX_ID_SST;

  dhSst;
  layerSst;
  //Sstタイムコントローラー
  sstTimeController;
  //タイムコントローラーのリスト
  timeList;
  //自動更新フラグ
  autoRefresh = true;

  dhAutoUpdate = true;

  validtimeInfo = '---';
  visible = false;

  ready = false;
  etd;
  elapsed = 0;
  dispatcher;
  visible;
  errorMessageInfo;
  transmittance = 47;

  constructor() {
    this.layerSst = new WRAP.Geo.Layer("SST");
    this.layerSst.setVisible(false);
    this.layerSst.disableInteraction(true);

    this.sstTimeController = new TimeController(WX_ID_SST, {
      label: 'Sea Surface Temperature',
      getTimeList: () => {
        if (this.timeList) {
          return this.timeList;
        }
        this._updateTimeList();
        return this.timeList;
      },
      updated: () => {
        this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
      },
      changeAutoRefresh: (autoFlg) => {
        this.setAutoRefresh(autoFlg);
      }
    });
  }

  setDispatcher(dispatcher) {
    this.dispatcher = dispatcher;
    this.missingDataObserver = new MissingDataObserver(this.dispatcher, MISSING_THRESHOLD, this);
  }

  async initialize() {
    await this.initData();
    this.initLayer();
    this.initTimeController();
    this.ready = true;
  }

  initData() {
    return new Promise(resolve => {
      this.dhSst = new WRAP.Data(configSstDh);
      this.dhSst.validate((displayTime, data, content) => {
        return this._validateTime(data, displayTime, content);
      });
      this.missingDataObserver.monitoring(WX_ID_SST, this.dhSst, this.layerSst);

      this.dhSst.updated('validtime', ref => {
        this._selectValidTime(ref);
        resolve(ref);
      });
      this.dhSst.updated('timelist', () => {
        if (this.errorMessageInfo) {
          this.dispatcher({type: 'CLEAR_ERROR', payload: this.errorMessageInfo});
          this.errorMessageInfo = null;
        }
      }, () => {
        this.errorMessageInfo = this.dispatcher({type: 'SHOW_ERROR', payload: {
          message: COMMUNICATE_ERROR_MESSAGE_SST,
          replacement: {data: 'timelist'},
          additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
        }});
      });
    });
  }

  initLayer() {
    this.layerSst.configure(this.dhSst, configSstLayer);
  }

  initTimeController() {
    this.sstTimeController.addLayer(this.layerSst);
  }

  async drawLayer(visible) {
    this.visible = visible;
    if (!this.ready) {
      await this.initialize();
    }
    this.setVisible(visible);
  }

  getLayer() {
    return this.layerSst;
  }

  setVisible(visible) {
    if (!this.ready) {
      return;
    }
    if (this.layerSst.visible() === visible) {
      return;
    }

    if (visible) {
      this.layerSst.show();
    } else {
      this.layerSst.hide();
    }

    const enabled = visible && this.autoRefresh;
    if (this.dhAutoUpdate !== enabled) {
      this.dhSst.setAutoUpdate('validtime', enabled);
      this.dhAutoUpdate = enabled;
    }

    this._updateTimeList();
    this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
  }

  getTimeInformation() {
    return {
      id: Sst.NAME,
      label: 'Sea Surface Temperature',
      baseTime: {
        id: Sst.NAME,
        time: this.validtimeInfo
      },
    }
  }

  getLegend() {
    return {
      id: Sst.NAME,
      label: 'Sea Surface Temperature',
      component: LegendSst,
      contents: this
    }
  }

  getTimeController() {
    return this.sstTimeController;
  }

  applyElapsed(elapsed) {
    this.elapsed = elapsed;

    if (!this.ready) return;

    this._selectValidTime(this.dhSst.retained.validtime);
  }

  applyPlanETD(etd) {
    this.etd = etd;

    if (!this.ready) return;

    this._selectValidTime(this.dhSst.retained.validtime);
  }

  setAutoRefresh(autoFlg) {
    if (autoFlg === this.autoRefresh) return;
    this.autoRefresh = autoFlg;
    if (!this.ready) return;
    this.dhSst.setAutoUpdate('validtime', this.autoRefresh);
    this.dhAutoUpdate = this.autoRefresh;
  }

  /**
   * 自動更新モード時のvalid選択
   */
  _selectValidTime(ref) {
    let targetTime;
    if (!this.etd) {
      //プラン未選択時
      if (!this.autoRefresh) {
        //タイムコントローラ操作時にSstを追加した場合、タイムコントローラの選択時制
        targetTime = this.sstTimeController.getCurrentTime();
      } else {
        targetTime = Date.now();
      }
    } else {
      //プラン選択時
      targetTime = this.etd.getTime() + this.elapsed * 1000;
    }

    const vt = this._nearValidTime(ref, targetTime);
    if (vt) {
      if (this.validtimeInfo !== vt) {
        this.validtimeInfo = vt;
        this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
      }
      this._updateDataset();
    }
    this._updateTimeList();
  }

  /**
   * タイムコントロールモード時のvalid選択
   */
  _validateTime(data, displayTime, content) {
    const vt = this._nearValidTime(data.retained.validtime, displayTime);
    if (vt) {
      this.validtimeInfo = content.validtime = vt;
      return true;
    }
    return false
  }

  _nearValidTime(timeList, targetTime) {
    if (!timeList || timeList.validtime.length === 0) return null;
    timeList = timeList.validtime;

    let _targetTime;
    if (typeof targetTime === 'number') {
      _targetTime = targetTime
    } else {
      _targetTime = parseUTCDate(targetTime).getTime();
    }

    const tmpTimeList = timeList.reduce((accumulator, validtime) => {
      const validSec = parseUTCDate(validtime).getTime();
      accumulator.push(
        {
          "validtime": validtime,
          "validtime_msec": validSec
        }
      );
      return accumulator;
    }, []);

    this.latestValid = tmpTimeList[0].validtime;

    // targetTimeに最も近いvalidを選択する(直近が過去時制と未来時制で同時刻の場合、未来時制のValidデータを採用する。)
    const sortedTimelist = _orderBy(tmpTimeList, [
      o => {
        return Math.abs(_targetTime - o.validtime_msec);
      },
      'validtime'
    ], ['asc', 'desc']);

    const selectedTime = sortedTimelist[0];

    return selectedTime.validtime;
  }

  _updateDataset() {
    if (this.validtimeInfo !== '---') {
      /**
       * WRAP, CloudFrontのキャッシュ対策としてクエリストリングに最新のvalidtimeを付与する
       * timelistが更新された場合にvalidtimeを更新する
       */
      let latestValid;
      if (this.timeList && this.timeList[this.timeList.length - 1] !== this.latestValid) {
        latestValid = this.latestValid;
      } else {
        latestValid = this.validtimeInfo;
      }
      this.layerSst.set({validtime: this.validtimeInfo, latestValid: latestValid});
    }
  }

  _updateTimeList() {
    let validTimes = this.dhSst.retained?.validtime?.validtime;
    if (!validTimes) {
      return;
    }

    // // 最新から直近過去までのvalidにfilter
    // const now = new Date();
    // now.setMinutes(0, 0, 0);
    // const base = formatUTCDate(now, "%Y%m%dT%H%M%S");
    // validTimes = Array.from(validTimes).sort();
    // this.timeList = validTimes.filter(vt => {
    //   return base <= vt;
    // });
    this.timeList = Array.from(validTimes).sort();

    if (this.autoRefresh) {
      this.sstTimeController.setCurrentTime(this.validtimeInfo);
    }
    this.sstTimeController.adjustCurrentTime();
    this.sstTimeController.invalidate();
  }

  getTransmittance() {
    return this.transmittance;
  }

  setTransmittance(transmittance) {
    if (this.transmittance === transmittance) {
      return;
    }
    this.transmittance = transmittance;
    const opacity = Math.round(255 / 100 * (100 - this.transmittance));
    configSstLayer.Attributes.style.default.palette.slice(4, 12).forEach(palette => {
      palette[3] = opacity;
    });
    this.layerSst.invalidate();
  }
}

export default Sst;
